import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { billingErrorMessage } from './errorMessage.js';
import { varianceDriverLabel } from './varianceSummaryTableModel.js';

const MAX_VISIBLE_REFRESH_CHANGES = 25;
const driverLabel = (value) => varianceDriverLabel({ autoDriver: value }) || value || 'Unclassified';

const ChangesTable = ({ rows = [] }) => {
  if (!rows.length) return null;
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" aria-label="Variance classification changes">
        <TableHead>
          <TableRow>
            <TableCell>BAC</TableCell>
            <TableCell>Current</TableCell>
            <TableCell>Refreshed</TableCell>
            <TableCell align="right">Variance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, MAX_VISIBLE_REFRESH_CHANGES).map((row) => (
            <TableRow key={row.varianceId || row.bac}>
              <TableCell sx={{ fontWeight: 800 }}>{row.bac}</TableCell>
              <TableCell>{driverLabel(row.oldAutoDriver)}</TableCell>
              <TableCell>{driverLabel(row.newAutoDriver)}</TableCell>
              <TableCell align="right">${Number(row.maxAbsDelta || 0).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SkippedSummary = ({ rows = [] }) => {
  if (!rows.length) return null;
  const counts = rows.reduce((acc, row) => {
    const reason = row.reason || 'Skipped';
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});
  return (
    <Typography variant="caption" color="text.secondary">
      Skipped: {Object.entries(counts).map(([reason, count]) => `${reason.replaceAll('_', ' ')} (${count})`).join(', ')}
    </Typography>
  );
};

const VarianceClassificationRefreshDialog = ({
  open,
  includeReviewed,
  onIncludeReviewedChange,
  onClose,
  onPreview,
  onCommit,
  result,
  isRunning,
  error,
}) => {
  const changes = result?.changes || [];
  const skipped = result?.skipped || [];
  const canCommit = Boolean(result?.dryRun && changes.length && !isRunning && !error);
  return (
    <Dialog open={open} onClose={isRunning ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Refresh Variance Reasons</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {isRunning ? <LinearProgress /> : null}
          {error ? (
            <Alert severity="error">
              {billingErrorMessage(error, 'Variance reasons could not be refreshed.')}
            </Alert>
          ) : null}
          <FormControlLabel
            control={(
              <Checkbox
                checked={includeReviewed}
                onChange={(event) => onIncludeReviewedChange(event.target.checked)}
                disabled={isRunning}
              />
            )}
            label="Include reviewed variances"
          />
          {result ? (
            <Alert severity={changes.length ? 'warning' : 'success'}>
              {result.dryRun
                ? changes.length
                  ? `${changes.length.toLocaleString()} variance reason${changes.length === 1 ? '' : 's'} ready to refresh.`
                  : 'No stale variance reasons found.'
                : `${Number(result.updatedCount || 0).toLocaleString()} variance reason${Number(result.updatedCount || 0) === 1 ? '' : 's'} refreshed.`}
            </Alert>
          ) : null}
          <ChangesTable rows={changes} />
          {changes.length > MAX_VISIBLE_REFRESH_CHANGES ? (
            <Typography variant="caption" color="text.secondary">
              Showing first {MAX_VISIBLE_REFRESH_CHANGES} of {changes.length.toLocaleString()} changes.
            </Typography>
          ) : null}
          <SkippedSummary rows={skipped} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRunning}>Close</Button>
        <Button variant="outlined" onClick={onPreview} disabled={isRunning}>
          Preview
        </Button>
        <Button variant="contained" onClick={onCommit} disabled={!canCommit}>
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VarianceClassificationRefreshDialog;
